export interface MergeField {
  key: string;
  label: string;
  category: string;
  example: string;
}

export const MERGE_FIELDS: MergeField[] = [
  { key: "coach.name", label: "Coach Full Name", category: "Coach", example: "John Smith" },
  { key: "coach.first_name", label: "Coach First Name", category: "Coach", example: "John" },
  { key: "coach.last_name", label: "Coach Last Name", category: "Coach", example: "Smith" },
  { key: "coach.title", label: "Coach Title", category: "Coach", example: "Head Football Coach" },
  { key: "coach.sport", label: "Sport", category: "Coach", example: "Football" },
  { key: "coach.email", label: "Coach Email", category: "Coach", example: "jsmith@university.edu" },
  { key: "compensation.base_salary", label: "Base Annual Salary", category: "Compensation", example: "$500,000" },
  { key: "compensation.base_salary_words", label: "Base Salary (written)", category: "Compensation", example: "Five Hundred Thousand Dollars" },
  { key: "compensation.signing_bonus", label: "Signing Bonus", category: "Compensation", example: "$50,000" },
  { key: "compensation.total_bonuses", label: "Total Bonuses", category: "Compensation", example: "$75,000" },
  { key: "compensation.total_allowances", label: "Total Allowances", category: "Compensation", example: "$12,000" },
  { key: "term.start_date", label: "Contract Start Date", category: "Term", example: "July 1, 2024" },
  { key: "term.end_date", label: "Contract End Date", category: "Term", example: "June 30, 2027" },
  { key: "term.years", label: "Contract Duration (years)", category: "Term", example: "3" },
  { key: "term.contract_type", label: "Contract Type", category: "Term", example: "Head Coach" },
  { key: "buyout.clause_amount", label: "Buyout Clause Amount", category: "Buyout", example: "$1,500,000" },
  { key: "buyout.year_1_amount", label: "Buyout Year 1 Amount", category: "Buyout", example: "$1,500,000" },
  { key: "buyout.year_2_amount", label: "Buyout Year 2 Amount", category: "Buyout", example: "$1,000,000" },
  { key: "buyout.year_3_amount", label: "Buyout Year 3 Amount", category: "Buyout", example: "$500,000" },
  { key: "org.name", label: "Organization Name", category: "Institution", example: "State University" },
  { key: "org.institution_name", label: "Institution Name", category: "Institution", example: "State University Athletic Department" },
  { key: "org.division", label: "Division", category: "Institution", example: "Division I" },
  { key: "org.conference", label: "Conference", category: "Institution", example: "Big Ten Conference" },
  { key: "contract.id", label: "Contract ID", category: "Contract", example: "123" },
  { key: "contract.status", label: "Contract Status", category: "Contract", example: "Active" },
];

export interface ContractData {
  id?: number;
  coachName?: string;
  coachFirstName?: string;
  coachLastName?: string;
  coachTitle?: string;
  coachSport?: string;
  coachEmail?: string;
  annualSalary?: number | null;
  signingBonus?: number | null;
  totalBonuses?: number;
  totalAllowances?: number;
  startDate?: string;
  endDate?: string;
  contractType?: string;
  buyoutClause?: number | null;
  buyoutSchedule?: { year: number; amount: number }[];
  status?: string;
  orgName?: string;
  orgInstitutionName?: string;
  orgDivision?: string;
  orgConference?: string;
}

function fmt(n: number | null | undefined): string {
  if (n == null) return "—";
  return `$${n.toLocaleString("en-US")}`;
}

function fmtDate(d: string | undefined): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

function capitalize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function numberToWords(n: number): string {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  if (n === 0) return "Zero";
  if (n < 0) return "Negative " + numberToWords(-n);

  let result = "";
  if (n >= 1_000_000) {
    result += numberToWords(Math.floor(n / 1_000_000)) + " Million ";
    n %= 1_000_000;
  }
  if (n >= 1_000) {
    result += numberToWords(Math.floor(n / 1_000)) + " Thousand ";
    n %= 1_000;
  }
  if (n >= 100) {
    result += ones[Math.floor(n / 100)] + " Hundred ";
    n %= 100;
  }
  if (n >= 20) {
    result += tens[Math.floor(n / 10)] + " ";
    n %= 10;
  }
  if (n > 0) {
    result += ones[n] + " ";
  }
  return result.trim();
}

function contractYears(startDate: string | undefined, endDate: string | undefined): string {
  if (!startDate || !endDate) return "—";
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const years = Math.round((end.getTime() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    return String(years);
  } catch {
    return "—";
  }
}

export function buildMergeContext(data: ContractData): Record<string, string> {
  const nameParts = data.coachName?.split(" ") ?? [];
  const firstName = data.coachFirstName ?? nameParts[0] ?? "";
  const lastName = data.coachLastName ?? nameParts.slice(1).join(" ") ?? "";

  const scheduleYear = (year: number): string => {
    const entry = data.buyoutSchedule?.find((e) => e.year === year);
    if (entry) return fmt(entry.amount);
    return data.buyoutClause != null ? fmt(data.buyoutClause) : "—";
  };

  const salaryWords = data.annualSalary != null
    ? numberToWords(Math.round(data.annualSalary)) + " Dollars"
    : "—";

  return {
    "coach.name": data.coachName ?? "—",
    "coach.first_name": firstName,
    "coach.last_name": lastName,
    "coach.title": data.coachTitle ?? "—",
    "coach.sport": data.coachSport ?? "—",
    "coach.email": data.coachEmail ?? "—",
    "compensation.base_salary": fmt(data.annualSalary),
    "compensation.base_salary_words": salaryWords,
    "compensation.signing_bonus": fmt(data.signingBonus),
    "compensation.total_bonuses": fmt(data.totalBonuses ?? 0),
    "compensation.total_allowances": fmt(data.totalAllowances ?? 0),
    "term.start_date": fmtDate(data.startDate),
    "term.end_date": fmtDate(data.endDate),
    "term.years": contractYears(data.startDate, data.endDate),
    "term.contract_type": capitalize(data.contractType ?? ""),
    "buyout.clause_amount": fmt(data.buyoutClause),
    "buyout.year_1_amount": scheduleYear(1),
    "buyout.year_2_amount": scheduleYear(2),
    "buyout.year_3_amount": scheduleYear(3),
    "org.name": data.orgName ?? "—",
    "org.institution_name": data.orgInstitutionName ?? data.orgName ?? "—",
    "org.division": data.orgDivision ?? "—",
    "org.conference": data.orgConference ?? "—",
    "contract.id": String(data.id ?? "—"),
    "contract.status": capitalize(data.status ?? ""),
  };
}

export function renderMergeFields(body: string, context: Record<string, string>): string {
  return body.replace(/\{\{([^}]+)\}\}/g, (match, key: string) => {
    const trimmed = key.trim();
    return context[trimmed] ?? match;
  });
}

export function getMergeFieldCategories(): string[] {
  return Array.from(new Set(MERGE_FIELDS.map((f) => f.category)));
}
